import { useEffect, useState } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const formatTime = value => value ? new Date(value).toLocaleString("en-GB", {
  timeZone: "Asia/Dhaka", dateStyle: "medium", timeStyle: "short",
}) : "Unavailable";

export default function AdminVisitors({ token }) {
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
    fetch(`${base}/visitors?page=${page}`, {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error("Unable to load visitors. Check the backend deployment and database migration.");
      return response.json();
    }).then(data => {
      if (!controller.signal.aborted) setResult(data);
    }).catch(err => {
      if (!controller.signal.aborted) setError(err.message);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [token, page, refresh]);

  return (
    <section className="max-w-6xl mx-auto my-6 bg-white rounded-xl shadow p-4 sm:p-6" aria-label="Visitor analytics">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-mm-secondery flex items-center gap-2">
            <VisibilityOutlinedIcon /> Total visitors: {result ? result.total.toLocaleString() : "—"}
          </h2>
          <p className="text-sm text-gray-500 mb-0">Unique browsers · Approximate location · Times in Bangladesh (UTC+6)</p>
        </div>
        <button disabled={loading} onClick={() => setRefresh(value => value + 1)} className="bg-mm-secondery text-white rounded px-3 py-2 disabled:opacity-50">Refresh</button>
      </div>
      {error ? <p role="alert" className="text-red-600">{error}</p> : loading ? <p role="status">Loading visitors…</p> : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600"><tr>
                <th className="p-3">Visitor</th><th className="p-3">Location</th>
                <th className="p-3">First visit</th><th className="p-3">Latest visit</th>
              </tr></thead>
              <tbody>
                {result?.visitors.map((visitor, index) => (
                  <tr key={visitor.visitor_id} className="border-b border-gray-100">
                    <td className="p-3">{(page - 1) * result.pageSize + index + 1}</td>
                    <td className="p-3">{[visitor.city, visitor.region, visitor.country].filter(Boolean).join(", ") || "Unavailable"}</td>
                    <td className="p-3 whitespace-nowrap">{formatTime(visitor.first_seen_at)}</td>
                    <td className="p-3 whitespace-nowrap">{formatTime(visitor.last_seen_at)}</td>
                  </tr>
                ))}
                {result?.visitors.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-gray-500">No visitors recorded yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-3 mt-4">
            <button disabled={page === 1} onClick={() => setPage(value => value - 1)} className="border rounded px-3 py-1 disabled:opacity-40">Previous</button>
            <span>Page {page}</span>
            <button disabled={!result || page * result.pageSize >= result.total} onClick={() => setPage(value => value + 1)} className="border rounded px-3 py-1 disabled:opacity-40">Next</button>
          </div>
        </>
      )}
    </section>
  );
}
